
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, RotateCcw, Eye, EyeOff, Users, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { Contact } from '@/pages/PersonalizeOutreach';

interface ResultsExportProps {
  contacts: Contact[];
  onReset: () => void;
}

const ResultsExport = ({ contacts, onReset }: ResultsExportProps) => {
  const [expandedContacts, setExpandedContacts] = useState<Set<string>>(new Set());

  const toggleExpanded = (contactId: string) => {
    setExpandedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  const exportToCSV = () => {
    const headers = [
      'Person',
      'Organization', 
      'Website',
      'Email',
      'Title',
      'Phone',
      'AI Research',
      'Personalized Message'
    ];

    const csvContent = [
      headers.join(','),
      ...contacts.map(contact => [
        `"${contact.person || ''}"`,
        `"${contact.organization || ''}"`,
        `"${contact.website || ''}"`,
        `"${contact.email || ''}"`,
        `"${contact.title || ''}"`,
        `"${contact.phone || ''}"`,
        `"${(contact.research || '').replace(/"/g, '""')}"`,
        `"${(contact.personalizedMessage || '').replace(/"/g, '""')}"`,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `personalized-outreach-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV file downloaded successfully!');
  };

  const completedContacts = contacts.filter(c => c.research && c.personalizedMessage);
  const failedContacts = contacts.length - completedContacts.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Completed</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{completedContacts.length}</p>
              <p className="text-sm text-green-700">Successfully researched</p>
            </div>
            
            {failedContacts > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-900">Failed</span>
                </div>
                <p className="text-2xl font-bold text-red-900">{failedContacts}</p>
                <p className="text-sm text-red-700">Need manual review</p>
              </div>
            )}
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Ready to Send</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{contacts.length}</p>
              <p className="text-sm text-blue-700">Total contacts</p>
            </div>
          </div>

          <div className="flex gap-4">
            <Button onClick={exportToCSV} size="lg" className="flex-1 md:flex-none">
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
            <Button onClick={onReset} variant="outline" size="lg">
              <RotateCcw className="h-4 w-4 mr-2" />
              Start New Campaign
            </Button>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Preview Results</h3>
            <div className="grid gap-4 max-h-96 overflow-y-auto">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium">{contact.person}</h4>
                      <p className="text-sm text-gray-600">{contact.organization}</p>
                      {contact.title && (
                        <p className="text-xs text-gray-500">{contact.title}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={contact.research && contact.personalizedMessage ? 'default' : 'destructive'}
                      >
                        {contact.research && contact.personalizedMessage ? 'Complete' : 'Failed'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpanded(contact.id)}
                      >
                        {expandedContacts.has(contact.id) ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {expandedContacts.has(contact.id) && (
                    <div className="space-y-4 pt-3 border-t">
                      {contact.research && (
                        <div>
                          <h5 className="font-medium text-sm mb-2">AI Research:</h5>
                          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                            {contact.research}
                          </p>
                        </div>
                      )}
                      
                      {contact.personalizedMessage && (
                        <div>
                          <h5 className="font-medium text-sm mb-2">Personalized Message:</h5>
                          <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded">
                            {contact.personalizedMessage}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultsExport;
